import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const StoreLayoutTab = ({ stores, onStoreUpdate }) => {
  const [selectedStore, setSelectedStore] = useState(null);
  const [activeStoreTab, setActiveStoreTab] = useState('current');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadData, setUploadData] = useState({
    file: null,
    changelog: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState('all');

  // Mock layout data
  const [storeLayouts, setStoreLayouts] = useState({
    'STR001': [
      {
        id: 'layout_001',
        version: 'v2.1',
        fileName: 'dhanmondi-store-layout-current.pdf',
        uploadDate: '2024-09-15',
        uploadedBy: 'John Doe',
        isCurrent: true,
        changelog: 'Updated product placement and customer flow areas'
      },
      {
        id: 'layout_002',
        version: 'v2.0',
        fileName: 'dhanmondi-store-layout-previous.pdf',
        uploadDate: '2024-08-20',
        uploadedBy: 'Jane Smith',
        isCurrent: false,
        changelog: 'Initial layout design with main sections'
      }
    ],
    'STR002': [
      {
        id: 'layout_003',
        version: 'v1.0',
        fileName: 'gulshan-store-layout.pdf',
        uploadDate: '2024-09-10',
        uploadedBy: 'Mike Johnson',
        isCurrent: true,
        changelog: 'Premium store layout with enhanced display areas'
      }
    ]
  });

  const filteredStores = stores?.filter(store => {
    const matchesSearch = store?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         store?.id?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         store?.location?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesLocation = filterLocation === 'all' || store?.location?.toLowerCase() === filterLocation?.toLowerCase();
    return matchesSearch && matchesLocation;
  });

  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    { value: 'dhaka', label: 'Dhaka' },
    { value: 'chittagong', label: 'Chittagong' },
    { value: 'sylhet', label: 'Sylhet' },
    { value: 'rajshahi', label: 'Rajshahi' },
    { value: 'khulna', label: 'Khulna' }
  ];

  const storeTabs = [
    { id: 'current', label: 'Current Layout', icon: 'Eye' },
    { id: 'history', label: 'Version History', icon: 'Clock' }
  ];

  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    setActiveStoreTab('current');
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes?.includes(file?.type)) {
        alert('Please upload a valid file (JPG, PNG, JPEG, PDF)');
        return;
      }
      // Validate file size (max 10MB)
      if (file?.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setUploadData(prev => ({ ...prev, file }));
    }
  };

  const handleUploadSubmit = () => {
    if (!uploadData?.file || !selectedStore) return;

    const currentLayouts = storeLayouts?.[selectedStore?.id] || [];
    const currentVersion = currentLayouts?.length > 0 ? 
      parseFloat(currentLayouts?.[0]?.version?.replace('v', '')) : 0;
    const newVersion = `v${(currentVersion + 0.1)?.toFixed(1)}`;

    const newLayout = {
      id: `layout_${Date.now()}`,
      version: newVersion,
      fileName: uploadData?.file?.name,
      uploadDate: new Date()?.toISOString()?.split('T')?.[0],
      uploadedBy: 'Current User',
      isCurrent: true,
      changelog: uploadData?.changelog || 'Layout file updated'
    };

    // Update store layouts
    setStoreLayouts(prev => {
      const updatedLayouts = [...(prev?.[selectedStore?.id] || [])];
      // Set previous layouts to not current
      updatedLayouts?.forEach(layout => layout.isCurrent = false);
      // Add new layout at the beginning
      updatedLayouts?.unshift(newLayout);
      
      return {
        ...prev,
        [selectedStore?.id]: updatedLayouts
      };
    });

    // Update store's hasCurrentLayout status
    const updatedStore = { ...selectedStore, hasCurrentLayout: true };
    onStoreUpdate?.(updatedStore);

    // Reset upload data and close modal
    setUploadData({ file: null, changelog: '' });
    setIsUploadModalOpen(false);
  };

  const getCurrentLayout = (storeId) => {
    const layouts = storeLayouts?.[storeId] || [];
    return layouts?.find(layout => layout?.isCurrent);
  };

  const getLayoutHistory = (storeId) => {
    const layouts = storeLayouts?.[storeId] || [];
    return layouts?.filter(layout => !layout?.isCurrent)?.slice(0, 5); // Show last 5 versions
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'FileText';
      case 'jpg': case'jpeg': case'png': return 'Image';
      default: return 'File';
    }
  };

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="bg-card rounded-lg border border-border p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            type="text"
            placeholder="Search stores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full"
          />
          <Select
            placeholder="Filter by location"
            options={locationOptions}
            value={filterLocation}
            onChange={setFilterLocation}
          />
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {filteredStores?.length} store{filteredStores?.length !== 1 ? 's' : ''} found
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Store List */}
        <div className="lg:col-span-1">
          <div className="bg-card rounded-lg border border-border">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-medium text-foreground">Store Layouts</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Select a store to view and manage its layouts
              </p>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {filteredStores?.map((store) => {
                const currentLayout = getCurrentLayout(store?.id);
                return (
                  <div
                    key={store?.id}
                    onClick={() => handleStoreSelect(store)}
                    className={`p-4 border-b border-border cursor-pointer transition-colors hover:bg-muted/50 ${
                      selectedStore?.id === store?.id ? 'bg-primary/10 border-l-4 border-l-primary' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-foreground">{store?.name}</div>
                        <div className="text-sm text-muted-foreground">{store?.location} • {store?.id}</div>
                        {currentLayout ? (
                          <div className="text-xs text-success mt-1 flex items-center gap-1">
                            <Icon name="CheckCircle" size={12} />
                            Current: {currentLayout?.version}
                          </div>
                        ) : (
                          <div className="text-xs text-warning mt-1 flex items-center gap-1">
                            <Icon name="AlertCircle" size={12} />
                            No layout uploaded
                          </div>
                        )}
                      </div>
                      <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Layout Details */}
        <div className="lg:col-span-2">
          {selectedStore ? (
            <div className="bg-card rounded-lg border border-border">
              {/* Header */}
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">{selectedStore?.name}</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedStore?.location} • {selectedStore?.address}
                    </p>
                  </div>
                  <Button
                    variant="default"
                    iconName="Upload"
                    iconPosition="left"
                    onClick={() => setIsUploadModalOpen(true)}
                  >
                    Upload New Version
                  </Button>
                </div>
              </div>

              {/* Store Tabs */}
              <div className="border-b border-border">
                <div className="flex overflow-x-auto px-6">
                  {storeTabs?.map((tab) => (
                    <Button
                      key={tab?.id}
                      variant="ghost"
                      onClick={() => setActiveStoreTab(tab?.id)}
                      className={`flex-shrink-0 px-4 py-3 rounded-none border-b-2 transition-colors ${
                        activeStoreTab === tab?.id
                          ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} className="mr-2" />
                      {tab?.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeStoreTab === 'current' && (
                  <div className="space-y-6">
                    {getCurrentLayout(selectedStore?.id) ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-medium text-foreground">Current Layout</h3>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Download"
                            iconPosition="left"
                            onClick={() => {
                              // Download current layout file
                              console.log('Download layout:', getCurrentLayout(selectedStore?.id));
                            }}
                          >
                            Download
                          </Button>
                        </div>

                        {/* Current Layout Preview */}
                        <div className="border border-border rounded-lg p-6 bg-muted/30">
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                              <Icon name={getFileIcon(getCurrentLayout(selectedStore?.id)?.fileName)} size={24} className="text-primary" />
                            </div>
                            <div>
                              <div className="font-medium text-foreground">{getCurrentLayout(selectedStore?.id)?.fileName}</div>
                              <div className="text-sm text-muted-foreground">
                                Version {getCurrentLayout(selectedStore?.id)?.version} • 
                                Uploaded on {formatDate(getCurrentLayout(selectedStore?.id)?.uploadDate)} by {getCurrentLayout(selectedStore?.id)?.uploadedBy}
                              </div>
                            </div>
                          </div>

                          {/* Layout Preview Placeholder */}
                          <div className="bg-white border border-border rounded-lg p-8 text-center">
                            <Icon name="Image" size={48} className="text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground mb-2">Layout Preview</p>
                            <p className="text-xs text-muted-foreground">
                              {getCurrentLayout(selectedStore?.id)?.changelog}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon name="Upload" size={32} className="text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-medium text-foreground mb-2">No Layout Available</h3>
                        <p className="text-muted-foreground mb-4">
                          This store doesn't have a layout file uploaded yet.
                        </p>
                        <Button
                          variant="default"
                          iconName="Upload"
                          iconPosition="left"
                          onClick={() => setIsUploadModalOpen(true)}
                        >
                          Upload First Layout
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {activeStoreTab === 'history' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-foreground">Version History</h3>
                    
                    {getLayoutHistory(selectedStore?.id)?.length > 0 ? (
                      <div className="space-y-3">
                        {getLayoutHistory(selectedStore?.id)?.map((layout) => (
                          <div key={layout?.id} className="border border-border rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                                  <Icon name={getFileIcon(layout?.fileName)} size={20} className="text-secondary" />
                                </div>
                                <div>
                                  <div className="font-medium text-foreground flex items-center gap-2">
                                    {layout?.fileName}
                                    <span className="px-2 py-0.5 bg-secondary/10 text-secondary text-xs rounded font-medium">
                                      {layout?.version}
                                    </span>
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    Uploaded on {formatDate(layout?.uploadDate)} by {layout?.uploadedBy}
                                  </div>
                                  {layout?.changelog && (
                                    <div className="text-xs text-muted-foreground mt-1">
                                      {layout?.changelog}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <Icon name="Download" size={16} />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <Icon name="Eye" size={16} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon name="Clock" size={24} className="text-muted-foreground" />
                        </div>
                        <p className="text-muted-foreground">No version history available</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-card rounded-lg border border-border p-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Layout" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">Select a Store</h3>
              <p className="text-muted-foreground">
                Choose a store from the list to view and manage its layout files.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border shadow-elevated w-full max-w-md">
            {/* Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Upload New Version</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedStore?.name} - {selectedStore?.location}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setIsUploadModalOpen(false);
                    setUploadData({ file: null, changelog: '' });
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Store Layout File *
                </label>
                <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="layout-file-upload"
                  />
                  <label 
                    htmlFor="layout-file-upload"
                    className="cursor-pointer block"
                  >
                    {uploadData?.file ? (
                      <div className="flex items-center gap-2 justify-center">
                        <Icon name={getFileIcon(uploadData?.file?.name)} size={20} className="text-primary" />
                        <span className="text-sm text-foreground">{uploadData?.file?.name}</span>
                      </div>
                    ) : (
                      <div>
                        <Icon name="Upload" size={24} className="text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          JPG, PNG, JPEG, PDF (Max 10MB)
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Changelog */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Changelog (Optional)
                </label>
                <textarea
                  value={uploadData?.changelog}
                  onChange={(e) => setUploadData(prev => ({ ...prev, changelog: e?.target?.value }))}
                  placeholder="Describe what changed in this version..."
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-none h-20"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-border flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setIsUploadModalOpen(false);
                  setUploadData({ file: null, changelog: '' });
                }}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                disabled={!uploadData?.file}
                onClick={handleUploadSubmit}
                iconName="Upload"
                iconPosition="left"
              >
                Upload Layout
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreLayoutTab;